import { useCallback, useRef } from 'react';

import { CursorConfig } from 'atoms/cursor/config';

interface SpringResult {
	value: number;
	velocity: number;
}

function spring(
	cur: number,
	tgt: number,
	vel: number,
	stiff: number,
	damp: number
): SpringResult {
	const v = (vel + (tgt - cur) * stiff) * damp;

	return { value: cur + v, velocity: v };
}

export interface SpringPullState {
	cx: number;
	cy: number;
	tx: number;
	ty: number;
	vx: number;
	vy: number;

	active: boolean;
}

interface UseSpringTransformReturnType {
	pull: (el: HTMLElement, tx: number, ty: number) => void;
	release: (el: HTMLElement) => void;
	tick: () => void;
}

export function useSpringTransform(): UseSpringTransformReturnType {
	const states = useRef<Map<HTMLElement, SpringPullState>>(new Map());

	const pull = useCallback((el: HTMLElement, tx: number, ty: number): void => {
		if (!states.current.has(el)) {
			states.current.set(el, {
				cx: 0,
				cy: 0,
				tx: 0,
				ty: 0,
				vx: 0,
				vy: 0,

				active: true,
			});
		}

		const s = states.current.get(el)!;

		s.tx = tx;
		s.ty = ty;
		s.active = true;
	}, []);

	const release = useCallback((el: HTMLElement): void => {
		const s = states.current.get(el);

		if (s) {
			s.tx = 0;
			s.ty = 0;
			s.active = false;
		}
	}, []);

	const tick = useCallback((): void => {
		for (const [targetNode, s] of states.current) {
			const st = s.active
				? CursorConfig.pullStiffness
				: CursorConfig.releaseStiffness;

			const dm = s.active
				? CursorConfig.pullDamping
				: CursorConfig.releaseDamping;

			const sx = spring(s.cx, s.tx, s.vx, st, dm);
			const sy = spring(s.cy, s.ty, s.vy, st, dm);

			s.cx = sx.value;
			s.vx = sx.velocity;
			s.cy = sy.value;
			s.vy = sy.velocity;

			targetNode.style.setProperty('--spring-x', `${s.cx}px`);
			targetNode.style.setProperty('--spring-y', `${s.cy}px`);

			if (
				!s.active &&
				Math.abs(s.cx) < 0.05 &&
				Math.abs(s.vx) < 0.01 &&
				Math.abs(s.cy) < 0.05 &&
				Math.abs(s.vy) < 0.01
			) {
				targetNode.style.removeProperty('--spring-x');
				targetNode.style.removeProperty('--spring-y');

				states.current.delete(targetNode);
			}
		}
	}, []);

	return { pull, release, tick };
}
